/**
 * the source https://github.com/DataDog/rum-react-integration-examples/blob/master/src/utils/getGlobalObject.ts
 *
 * A utility function that retrieves the global object in any JavaScript environment.
 * This function first checks if the `globalThis` property is available, which is the standard way to access the global object.
 * If `globalThis` is not available, it uses a workaround by temporarily adding a property to `Object.prototype` and accessing it.
 * This workaround is necessary because in some environments, like Safari, the global object is not accessible through standard means.
 * The function finally falls back to checking other known global properties like `self`.
 *
 * @function
 * @returns {T} - The global object in the current JavaScript environment.
 *
 * @throws {Error} - If no global object can be determined, an error is thrown.
 */
export function getGlobalObject<T>(): T {
  if (typeof globalThis === 'object') {
    return globalThis as unknown as T;
  }
  Object.defineProperty(Object.prototype, '_dd_temp_', {
    get() {
      return this as object;
    },
    configurable: true,
  });
  // @ts-ignore _dd_temp is defined using defineProperty
  let globalObject: unknown = _dd_temp_;
  // @ts-ignore _dd_temp is defined using defineProperty
  delete Object.prototype._dd_temp_;
  if (typeof globalObject !== 'object') {
    // on safari _dd_temp_ is available on window but not globally
    // fallback on other browser globals check
    if (typeof self === 'object') {
      globalObject = self;
    } else if (typeof window === 'object') {
      globalObject = window;
    } else {
      globalObject = {};
    }
  }

  return globalObject as T;
}
